import { FloorTable, TableShape } from '../types/floorPlan.model'

export interface SeatPos {
  x: number
  y: number
}

export const SEAT_RADIUS = 9
export const SEAT_GAP = 6
export const SEAT_HALO_EXTRA = 4
export const SEAT_OUTER_RADIUS = SEAT_RADIUS + SEAT_HALO_EXTRA // halo radius
export const SEAT_MIN_GAP = 1.5 // px kept between neighbouring halos

/**
 * Returns seat positions in the table's LOCAL coordinate space (origin at the
 * table center). Because seats live inside the table's Konva Group, they
 * automatically follow drag / rotation / resize of the table.
 */
export function getSeatPositions(
  shape: TableShape,
  seatCount: number,
  width: number,
  height: number
): SeatPos[] {
  if (seatCount <= 0) return []

  if (shape === 'round') {
    return getRoundSeatPositions(seatCount, width, height)
  }

  return getRectSeatPositions(seatCount, width, height)
}

function getRoundSeatPositions(
  seatCount: number,
  width: number,
  height: number
): SeatPos[] {
  const radius = Math.max(width, height) / 2 + SEAT_GAP + SEAT_RADIUS
  const positions: SeatPos[] = []

  for (let i = 0; i < seatCount; i += 1) {
    // start at the top (−90°) and go clockwise
    const angle = (2 * Math.PI * i) / seatCount - Math.PI / 2
    positions.push({
      x: radius * Math.cos(angle),
      y: radius * Math.sin(angle)
    })
  }

  return positions
}

function getRectSeatPositions(
  seatCount: number,
  width: number,
  height: number
): SeatPos[] {
  const hw = width / 2
  const hh = height / 2
  const offset = SEAT_GAP + SEAT_RADIUS

  // Distribute seats across the 4 sides proportionally to side length.
  // top/bottom weighted by width, left/right by height.
  const weights = [width, width, height, height] // top, bottom, left, right
  const perSide = distributeByWeight(seatCount, weights)
  const [top, bottom, left, right] = perSide

  const positions: SeatPos[] = []

  // top: y fixed above the table, x spread across the width
  spreadAlong(top).forEach(f => {
    positions.push({ x: -hw + f * width, y: -hh - offset })
  })
  // bottom
  spreadAlong(bottom).forEach(f => {
    positions.push({ x: -hw + f * width, y: hh + offset })
  })
  // left: x fixed to the left, y spread across the height
  spreadAlong(left).forEach(f => {
    positions.push({ x: -hw - offset, y: -hh + f * height })
  })
  // right
  spreadAlong(right).forEach(f => {
    positions.push({ x: hw + offset, y: -hh + f * height })
  })

  return positions
}

/**
 * Splits `total` into `weights.length` integer buckets proportionally to the
 * provided weights, using the largest-remainder method so the buckets always
 * sum exactly to `total`.
 */
function distributeByWeight(total: number, weights: number[]): number[] {
  const weightSum = weights.reduce((acc, w) => acc + w, 0)
  if (weightSum === 0) return weights.map(() => 0)

  const raw = weights.map(w => (total * w) / weightSum)
  const floors = raw.map(Math.floor)
  let remainder = total - floors.reduce((acc, n) => acc + n, 0)

  const order = raw
    .map((value, index) => ({ index, frac: value - Math.floor(value) }))
    .sort((a, b) => b.frac - a.frac)

  for (let i = 0; i < order.length && remainder > 0; i += 1) {
    floors[order[i].index] += 1
    remainder -= 1
  }

  return floors
}

/**
 * Returns `n` evenly spaced fractions in (0, 1), inset from both ends so seats
 * don't sit exactly on the table corners: (k + 1) / (n + 1).
 */
function spreadAlong(n: number): number[] {
  const fractions: number[] = []
  for (let k = 0; k < n; k += 1) {
    fractions.push((k + 1) / (n + 1))
  }
  return fractions
}

/** Smallest distance between any two seats. */
function minSeatDistance(seats: SeatPos[]): number {
  let min = Infinity
  for (let i = 0; i < seats.length; i += 1) {
    for (let j = i + 1; j < seats.length; j += 1) {
      const dist = Math.hypot(seats[i].x - seats[j].x, seats[i].y - seats[j].y)
      if (dist < min) min = dist
    }
  }
  return min
}

/** Shrink factor so neighbouring seat halos never overlap (mirrors SeatRing). */
export function getSeatScale(seats: SeatPos[]): number {
  if (seats.length < 2) return 1
  const allowed = minSeatDistance(seats) / 2 - SEAT_MIN_GAP
  return Math.min(1, Math.max(0.15, allowed / SEAT_OUTER_RADIUS))
}

/**
 * Full footprint (table body + seat ring incl. halos) as a box centred on the
 * table, in its own unrotated frame.
 */
export function getTableFootprint(
  shape: TableShape,
  seatCount: number,
  width: number,
  height: number
): { width: number; height: number } {
  const seats = getSeatPositions(shape, seatCount, width, height)
  if (seats.length === 0) return { width, height }

  const outer = SEAT_OUTER_RADIUS * getSeatScale(seats)
  let halfWidth = width / 2
  let halfHeight = height / 2
  seats.forEach(seat => {
    halfWidth = Math.max(halfWidth, Math.abs(seat.x) + outer)
    halfHeight = Math.max(halfHeight, Math.abs(seat.y) + outer)
  })
  return { width: halfWidth * 2, height: halfHeight * 2 }
}

/**
 * The outline a table takes up with its chairs, halos included: the convex
 * hull of the table body and every chair, as tight round them as a band
 * stretched over them. Two tables may touch at their outlines but not cross
 * them, so chairs never overlap, nor sit in the gaps between another table's
 * chairs, and nothing further apart is blocked: no slack, and no box with
 * empty corners or one that grows as a table turns.
 */
export type TableOutline = SeatPos[]

// A circle as a polygon drawn just outside it, so it holds the circle whole:
// with 16 sides it reaches at most 2% of the radius further.
const CIRCLE_SIDES = 16

function aroundCircle(x: number, y: number, radius: number): SeatPos[] {
  const reach = radius / Math.cos(Math.PI / CIRCLE_SIDES)
  return Array.from({ length: CIRCLE_SIDES }, (_, i) => {
    const angle = (2 * Math.PI * i) / CIRCLE_SIDES
    return { x: x + reach * Math.cos(angle), y: y + reach * Math.sin(angle) }
  })
}

// Andrew's monotone chain.
function convexHull(points: SeatPos[]): SeatPos[] {
  const sorted = [...points].sort((a, b) => a.x - b.x || a.y - b.y)
  const turn = (o: SeatPos, a: SeatPos, b: SeatPos) =>
    (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x)
  const half = (list: SeatPos[]) => {
    const chain: SeatPos[] = []
    list.forEach(point => {
      while (
        chain.length >= 2 &&
        turn(chain[chain.length - 2], chain[chain.length - 1], point) <= 0
      )
        chain.pop()
      chain.push(point)
    })
    chain.pop()
    return chain
  }
  return [...half(sorted), ...half([...sorted].reverse())]
}

/*
  The outline round the table's centre, unturned. Kept per table kind, since
  a drag checks every table in the room on each move and the hull depends on
  nothing else.
*/
const localOutlines = new Map<string, SeatPos[]>()

function localOutline(
  shape: TableShape,
  seatCount: number,
  width: number,
  height: number
): SeatPos[] {
  const key = `${shape}:${seatCount}:${width}:${height}`
  let outline = localOutlines.get(key)
  if (!outline) {
    outline = buildLocalOutline(shape, seatCount, width, height)
    localOutlines.set(key, outline)
  }
  return outline
}

function buildLocalOutline(
  shape: TableShape,
  seatCount: number,
  width: number,
  height: number
): SeatPos[] {
  const seats = getSeatPositions(shape, seatCount, width, height)
  const outer = SEAT_OUTER_RADIUS * getSeatScale(seats)
  const hw = width / 2
  const hh = height / 2
  const body =
    shape === 'round'
      ? aroundCircle(0, 0, Math.max(hw, hh))
      : [
          { x: -hw, y: -hh },
          { x: hw, y: -hh },
          { x: hw, y: hh },
          { x: -hw, y: hh }
        ]
  return convexHull([
    ...body,
    ...seats.flatMap(seat => aroundCircle(seat.x, seat.y, outer))
  ])
}

/** The outline of `table` standing with its centre at `center`. */
export function getTableOutline(
  table: Pick<
    FloorTable,
    'shape' | 'seatCount' | 'width' | 'height' | 'rotation'
  >,
  center: { x: number; y: number }
): TableOutline {
  const { shape, seatCount, width, height, rotation } = table
  // Clockwise on screen, as Konva turns the table.
  const rad = (rotation * Math.PI) / 180
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)
  return localOutline(shape, seatCount, width, height).map(point => ({
    x: center.x + point.x * cos - point.y * sin,
    y: center.y + point.x * sin + point.y * cos
  }))
}

// Separating axis test: two convex outlines are apart when, across one of
// their edges, their shadows do not overlap.
function apartAcrossAnEdgeOf(a: TableOutline, b: TableOutline): boolean {
  return a.some((point, i) => {
    const next = a[(i + 1) % a.length]
    const normal = { x: point.y - next.y, y: next.x - point.x }
    const shadow = (outline: TableOutline) => {
      const along = outline.map(p => p.x * normal.x + p.y * normal.y)
      return { min: Math.min(...along), max: Math.max(...along) }
    }
    const sa = shadow(a)
    const sb = shadow(b)
    return sa.max <= sb.min || sb.max <= sa.min
  })
}

/** True when the outlines cross; touching is allowed. */
export function outlinesOverlap(a: TableOutline, b: TableOutline): boolean {
  return !apartAcrossAnEdgeOf(a, b) && !apartAcrossAnEdgeOf(b, a)
}

/** A rectangle of the plan, such as the part of it on screen. */
export interface PlanArea {
  left: number
  top: number
  right: number
  bottom: number
}

function shiftAlong(min: number, max: number, start: number, end: number) {
  if (min < start) return start - min
  // Wider than the area: lined up with its start rather than pushed past it.
  if (max > end) return Math.max(end - max, start - min)
  return 0
}

/**
 * How far to move `outlines`, all together, so they lie inside `area`: zero
 * where they already do.
 */
export function shiftIntoArea(
  outlines: TableOutline[],
  area: PlanArea
): SeatPos {
  const points = outlines.flat()
  if (!points.length) return { x: 0, y: 0 }
  const xs = points.map(p => p.x)
  const ys = points.map(p => p.y)
  return {
    x: shiftAlong(Math.min(...xs), Math.max(...xs), area.left, area.right),
    y: shiftAlong(Math.min(...ys), Math.max(...ys), area.top, area.bottom)
  }
}
